// PartExForm.js
import React from "react";

export default function PartExForm({
  vehicleReg,
  onCancel,
  onSubmitted,
  isPosting,
  profile,
  setProfile,
  formId,
}) {
  const [partExReg, setPartExReg] = React.useState("");
  const [mileage, setMileage] = React.useState("");
  const [numKeys, setNumKeys] = React.useState("");
  const [serviceHistory, setServiceHistory] = React.useState("");
  const [condition, setCondition] = React.useState("");
  const [damage, setDamage] = React.useState("");
  const [outstandingFinance, setOutstandingFinance] = React.useState("");
  const [settlement, setSettlement] = React.useState("");
  const [valuation, setValuation] = React.useState("");
  const [essential, setEssential] = React.useState("");
  const [errors, setErrors] = React.useState({});

  if (formId === "sellMyCar" && essential !== "na") {
    setEssential("na");
  }

  const { fullName = "", telephone = "", email = "" } = profile || {};

  let formTitle = "Part Exchange Vehicle Details";
  if (formId === "sellMyCar") {
    formTitle = "Sell My Vehicle Details";
  }

  function validate() {
    const errs = {};
    // profile (unchanged)
    if (!fullName.trim()) errs.fullName = "Please enter your full name";
    if (!telephone.trim()) errs.telephone = "Please enter a phone number";
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email))
      errs.email = "Enter a valid email";

    // new + existing form fields
    if (!partExReg.trim()) errs.partExReg = "Please enter your registration";

    if (!mileage.toString().trim()) {
      errs.mileage = "Enter mileage";
    } else if (isNaN(Number(mileage)) || Number(mileage) < 0) {
      errs.mileage = "Mileage must be a non-negative number";
    }

    if (!numKeys.toString().trim()) {
      errs.numKeys = "Enter number of keys";
    } else if (!/^\d+$/.test(String(numKeys)) || Number(numKeys) < 0) {
      errs.numKeys = "Number of keys must be 0 or more";
    }

    if (!serviceHistory) errs.serviceHistory = "Select service history";
    if (!condition) errs.condition = "Select condition";
    if (!damage.trim()) errs.damage = "Enter details of damage/repairs";
    if (!outstandingFinance) errs.outstandingFinance = "Select Yes/No";

    // Settlement only required when finance = Yes
    if (outstandingFinance === "Yes") {
      if (!settlement.toString().trim()) {
        errs.settlement = "Enter settlement figure";
      } else if (isNaN(Number(settlement)) || Number(settlement) < 0) {
        errs.settlement = "Settlement must be a non-negative number";
      }
    }

    if (!valuation.trim()) errs.valuation = "Enter your valuation";
    if (!essential) errs.essential = "Select Yes/No";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      type: "partEx",
      vehicleReg,
      // profile-only fields
      fullName,
      telephone,
      email,
      // part-ex fields
      partExReg,
      mileage: mileage === "" ? null : Number(mileage),
      numKeys: numKeys === "" ? null : Number(numKeys),
      serviceHistory, // full / partial / none
      condition, // excellent / fair / poor
      damage,
      outstandingFinance, // Yes / No
      settlement: outstandingFinance === "Yes" ? Number(settlement) : null,
      valuation,
      essential, // Yes / No
      submittedAt: new Date().toISOString(),
      formId: formId || "partEx",
    };

    onSubmitted?.(payload);
  }

  const settlementDisabled =
    outstandingFinance === "No" || outstandingFinance === "";

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      <div style={formStyles.header}>{formTitle}</div>
      {/* Registration */}
      <label style={formStyles.label}>
        Registration
        <input
          style={formStyles.input}
          value={partExReg}
          onChange={(e) => setPartExReg(e.target.value)}
        />
        {errors.partExReg && (
          <div style={formStyles.error}>{errors.partExReg}</div>
        )}
      </label>
      {/* Mileage */}
      <label style={formStyles.label}>
        Mileage
        <input
          type="number"
          min="0"
          inputMode="numeric"
          style={formStyles.input}
          value={mileage}
          onChange={(e) => setMileage(e.target.value)}
        />
        {errors.mileage && <div style={formStyles.error}>{errors.mileage}</div>}
      </label>
      {/* Number of keys */}
      <label style={formStyles.label}>
        Number of keys
        <input
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          style={formStyles.input}
          value={numKeys}
          onChange={(e) => setNumKeys(e.target.value)}
        />
        {errors.numKeys && <div style={formStyles.error}>{errors.numKeys}</div>}
      </label>
      {/* Service history */}
      <label style={formStyles.label}>
        Service history
        <select
          style={formStyles.input}
          value={serviceHistory}
          onChange={(e) => setServiceHistory(e.target.value)}
        >
          <option value="">Select…</option>
          <option value="full">Full</option>
          <option value="partial">Partial</option>
          <option value="none">None</option>
        </select>
        {errors.serviceHistory && (
          <div style={formStyles.error}>{errors.serviceHistory}</div>
        )}
      </label>
      {/* Condition */}
      <label style={formStyles.label}>
        Condition
        <select
          style={formStyles.input}
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
        >
          <option value="">Select…</option>
          <option value="excellent">Excellent</option>
          <option value="fair">Fair</option>
          <option value="poor">Poor</option>
        </select>
        {errors.condition && (
          <div style={formStyles.error}>{errors.condition}</div>
        )}
      </label>
      {/* Notable damage */}
      <label style={formStyles.label}>
        Notable damage or repairs
        <input
          style={formStyles.input}
          value={damage}
          onChange={(e) => setDamage(e.target.value)}
        />
        {errors.damage && <div style={formStyles.error}>{errors.damage}</div>}
      </label>
      {/* Outstanding finance */}
      <label style={formStyles.label}>
        Is there any outstanding finance?
        <select
          style={formStyles.input}
          value={outstandingFinance}
          onChange={(e) => setOutstandingFinance(e.target.value)}
        >
          <option value="">Select…</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        {errors.outstandingFinance && (
          <div style={formStyles.error}>{errors.outstandingFinance}</div>
        )}
      </label>
      {/* Settlement figure – always visible, disabled when No */}
      <label style={formStyles.label}>
        Settlement figure
        <input
          type="number"
          min="0"
          inputMode="numeric"
          style={{
            ...formStyles.input,
            background: settlementDisabled ? "#f8fafc" : "#fff",
            cursor: settlementDisabled ? "not-allowed" : "text",
          }}
          value={settlement}
          onChange={(e) => setSettlement(e.target.value)}
          disabled={settlementDisabled}
          placeholder={
            settlementDisabled
              ? "Disabled unless finance = Yes"
              : "Enter settlement figure"
          }
        />
        {errors.settlement && (
          <div style={formStyles.error}>{errors.settlement}</div>
        )}
      </label>
      {/* Valuation */}
      <label style={formStyles.label}>
        Do you have a valuation in mind?
        <input
          style={formStyles.input}
          value={valuation}
          onChange={(e) => setValuation(e.target.value)}
        />
        {errors.valuation && (
          <div style={formStyles.error}>{errors.valuation}</div>
        )}
      </label>
      {/* Essential to deal */}
      {formId === "sellMyCar" ? (
        <input type="hidden" name="essential" value="na" />
      ) : (
        <label style={formStyles.label}>
          Is the part-exchange essential to the deal?
          <select
            style={formStyles.input}
            value={essential}
            onChange={(e) => setEssential(e.target.value)}
          >
            <option value="">Select…</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
          {errors.essential && (
            <div style={formStyles.error}>{errors.essential}</div>
          )}
        </label>
      )}
      {/* Profile (unchanged) */}
      <label style={formStyles.label}>
        Full name
        <input
          style={formStyles.input}
          value={fullName}
          onChange={(e) =>
            setProfile((p) => ({ ...p, fullName: e.target.value }))
          }
        />
        {errors.fullName && (
          <div style={formStyles.error}>{errors.fullName}</div>
        )}
      </label>
      <label style={formStyles.label}>
        Telephone
        <input
          style={formStyles.input}
          value={telephone}
          onChange={(e) =>
            setProfile((p) => ({ ...p, telephone: e.target.value }))
          }
        />
        {errors.telephone && (
          <div style={formStyles.error}>{errors.telephone}</div>
        )}
      </label>
      <label style={formStyles.label}>
        Email
        <input
          type="email"
          style={formStyles.input}
          value={email}
          onChange={(e) => setProfile((p) => ({ ...p, email: e.target.value }))}
        />
        {errors.email && <div style={formStyles.error}>{errors.email}</div>}
      </label>
      <div style={formStyles.actions}>
        <button
          type="button"
          onClick={onCancel}
          style={formStyles.secondaryBtn}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPosting}
          style={formStyles.primaryBtn}
        >
          {isPosting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </form>
  );
}

const formStyles = {
  form: {
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    background: "#fff",
  },
  header: {
    fontWeight: 700,
    marginBottom: 8,
  },
  label: {
    display: "block",
    fontSize: 12,
    color: "#334155",
    marginBottom: 8,
  },
  input: {
    width: "100%",
    marginTop: 4,
    padding: "8px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
  },
  error: {
    marginTop: 4,
    color: "#b91c1c",
    fontSize: 12,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 8,
  },
  primaryBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },
  secondaryBtn: {
    background: "#f1f5f9",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "8px 12px",
    cursor: "pointer",
    fontWeight: 600,
  },
};
